import {ellipsis} from 'polished';
import React, {FC} from 'react';
import styled from 'styled-components';

import {Icon} from '../../../../libs/shared/ui/src/components/icon/icon';
import {VisualSizesEnum} from '../../../../libs/shared/ui/src/styles/commonStyles';
import {fontSizes, fontWeights} from '../../../../libs/shared/ui/src/styles/fontStyles';
import {UserThemeConsumer} from '../../../../libs/shared/ui/src/theme/userThemeConsumer';
import {buildTeammateDisplayName} from '../../../helpers/modelHelpers';
import {getCardNoteDisplayDate} from '../../../helpers/models/cardNoteHelpers';
import {computeIsGlobalNamespace, isTeammateNamespace} from '../../../helpers/models/namespaceHelpers';
import {CardNoteModel} from '../../../models/api/cardNoteModel';
import {TeammateModel} from '../../../models/api/teammateModel';
import {TeamModel} from '../../../models/api/teamModel';
import {TeamAvatar} from '../../common/avatars/teamAvatar';
import {TeammateAvatar} from '../../common/avatars/teammateAvatar';
import {InteractiveDateTimeFormatterContainer} from '../../common/formatters/dateTime/interactiveDateTimeFormatterContainer';

/*
 * Props.
 */

interface ContactNoteFooterProps {
  note: CardNoteModel;
  teammate: TeammateModel | undefined;
  team: TeamModel | undefined;
}

/*
 * Styles.
 */

const StyledNoteFooterContainerDiv = styled.div`
  position: relative;
  display: flex;
  flex-flow: row;
  margin-top: 8px;
`;

const StyledNoteDetailsContainerDiv = styled.div`
  display: flex;
  flex-flow: row;
  margin-left: 8px;
  flex: 1;
  overflow: hidden;
`;

const StyledNameDiv = styled.div`
  flex: 1;
  line-height: 16px;
  font-size: ${fontSizes.verySmall};
  color: ${(p) => p.theme.greys.shade70};
  font-weight: ${fontWeights.medium};
  ${ellipsis()};
`;

const StyledDateAndAccessDiv = styled.div`
  display: flex;
  flex-flow: row;
`;

const StyledDateTimeContainerDiv = styled.div`
  font-size: ${fontSizes.veryTiny};
  line-height: ${fontSizes.large};
  display: flex;
  flex-flow: row;
  color: ${(p) => p.theme.greys.shade70};
  white-space: nowrap;
  padding-left: 6px;
  text-transform: uppercase;

  &:after {
    content: '•';
    margin: 0 6px;
  }
`;

const StyledIcon = styled(Icon)`
  margin-top: 2px;
`;

/*
 * Component.
 */

export const ContactNoteFooter: FC<ContactNoteFooterProps> = (props) => {
  const {note, teammate, team} = props;

  if (!teammate) {
    return null;
  }

  const name = buildTeammateDisplayName(teammate);

  return (
    <StyledNoteFooterContainerDiv>
      <TeammateAvatar teammate={teammate} size={VisualSizesEnum.SMALL} />
      <StyledNoteDetailsContainerDiv>
        <StyledNameDiv>{name}</StyledNameDiv>
        <StyledDateAndAccessDiv>
          <StyledDateTimeContainerDiv>
            <InteractiveDateTimeFormatterContainer value={getCardNoteDisplayDate(note)} />
          </StyledDateTimeContainerDiv>
          {renderNoteNamespace(note, team)}
        </StyledDateAndAccessDiv>
      </StyledNoteDetailsContainerDiv>
    </StyledNoteFooterContainerDiv>
  );
};

const renderNoteNamespace = (note: CardNoteModel, team: TeamModel | undefined) => {
  if (isTeammateNamespace(note.namespace)) {
    return (
      <UserThemeConsumer>
        {(theme) => <StyledIcon name="lockFilledNewTiny" size={12} color={theme.greys.shade70} />}
      </UserThemeConsumer>
    );
  }

  if (computeIsGlobalNamespace(note.namespace)) {
    return (
      <UserThemeConsumer>
        {(theme) => <StyledIcon name="sharedFilledTiny" size={12} color={theme.greys.shade70} />}
      </UserThemeConsumer>
    );
  }

  if (team) {
    return <TeamAvatar team={team} size={VisualSizesEnum.SMALL} />;
  }

  // Do not display anything
  return null;
};
